import React from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import { Field, reduxForm } from 'redux-form';
import { SelectField } from 'redux-form-material-ui';
import MenuItem from 'material-ui/MenuItem';
import RaisedButton from 'material-ui/RaisedButton';
import ModuleService from '../../../services/module';
import styles from '../../../styles/styles';
import style from './ApplicationForm.css';
import { renderTextField } from '../../../services/form';
import { SketchPicker } from 'react-color';
import LangService from '../../../services/lang';
import withTranslation from '../../../hoc/withTranslation';

const validate = values => {
    const errors = {};
    const requiredFields = [
        'name',
        'default_language',
    ];
    requiredFields.forEach(field => {
        if (!values[field]) {
            errors[field] = i18n.t('generic.required');
        }
    });

    const color = values['primary_color'];
    if (color && /(^#[0-9a-fA-F]{6}$)|(^#[0-9a-fA-F]{3}$)/i.test(color) === false) {
        errors['primary_color'] = i18n.t('page_app.wrong_primary_color');
    }
    const defaultLanguage = values['default_language'];
    if (!defaultLanguage || defaultLanguage.length < 2) {
        errors['default_language'] = i18n.t('page_app.default_language_empty');
    }
    const centerLatitude = values['center_latitude'];
    if (centerLatitude === 0 || centerLatitude === '0') {
        errors['center_latitude'] = i18n.t('page_app.center_latitude_eq_zero');
    }
    const centerLongitude = values['center_longitude'];
    if (centerLongitude === 0 || centerLongitude === '0') {
        errors['center_longitude'] = i18n.t('page_app.center_longitude_eq_zero');
    }
    return errors;
};

export class ApplicationForm extends React.Component {

    static propTypes = {
        onFormReset: PropTypes.func,
    };

    state = {
        displayPrimaryColorPicker: false,
        primaryColor: undefined,
        displaySecondaryColorPicker: false,
        secondaryColor: undefined,
        center_latitude: 45.776694,
        center_longitude: 3.0945805,
        android_package_name: null,
        ios_bundle_id: null,
        ios_app_store_id: null,
    };

    // Handlers

    handlePrimaryColorChangedFromField = (event) => {
        this.setState({ primaryColor: event.target.value });
    };

    handlePrimaryClick = () => {
        this.setState({ displayPrimaryColorPicker: !this.state.displayPrimaryColorPicker });
    };

    handlePrimaryClose = () => {
        this.setState({ displayPrimaryColorPicker: false });
    };

    handlePrimaryChange = (primaryColor) => {
        this.props.change('primary_color', primaryColor.hex);
    };

    handleSecondaryColorChangedFromField = (event) => {
        this.setState({ secondaryColor: event.target.value });
    };

    handleSecondaryClick = () => {
        this.setState({ displaySecondaryColorPicker: !this.state.displaySecondaryColorPicker });
    };

    handleSecondaryClose = () => {
        this.setState({ displaySecondaryColorPicker: false });
    };

    handleSecondaryChange = (secondaryColor) => {
        this.props.change('secondary_color', secondaryColor.hex);
    };

    render() {
        const {
            handleSubmit,
            pristine,
            reset,
            submitting,
            t,
        } = this.props;

        let modules = ModuleService.modules.map((module, index) => {
            return <MenuItem
                key={module.id}
                value={module.id}
                primaryText={ModuleService.translatedName(module)}
            />;
        });

        let languages = LangService.langs.map((lang, index) => {
            return <MenuItem
                key={lang.key}
                value={lang.key}
                primaryText={LangService.translatedName(lang.key)}
            />;
        });

        return (
            <form onSubmit={handleSubmit}>
                <div>
                    {/* <Field name="id" component={renderTextField} label="Id" fullWidth={true} readOnly={true}/>  */}
                    <Field
                        id="name"
                        name="name"
                        component={renderTextField}
                        label={t('generic.name')}
                        fullWidth={true}
                    />

                    <Field
                        id="primary_color"
                        name="primary_color"
                        component={renderTextField}
                        label={t('page_app.primary_color')}
                        fullWidth={true}
                        onChange={this.handlePrimaryColorChangedFromField}
                        onClick={this.handlePrimaryClick}
                    />
                    {this.state.displayPrimaryColorPicker && (
                        <div style={style.popover}>
                            <div style={style.cover} onClick={this.handlePrimaryClose} />
                            <SketchPicker
                                color={this.state.primaryColor}
                                onChange={this.handlePrimaryChange}
                            />
                        </div>
                    )}
                    <Field
                        id="secondary_color"
                        name="secondary_color"
                        component={renderTextField}
                        label={t('page_app.secondary_color')}
                        fullWidth={true}
                        onChange={this.handleSecondaryColorChangedFromField}
                        onClick={this.handleSecondaryClick}
                    />
                    {this.state.displaySecondaryColorPicker && (
                        <div style={style.popover}>
                            <div style={style.cover} onClick={this.handleSecondaryClose} />
                            <SketchPicker
                                color={this.state.secondaryColor}
                                onChange={this.handleSecondaryChange}
                            />
                        </div>
                    )}
                    <Field
                        id="modules"
                        name="modules"
                        component={SelectField}
                        multiple={true}
                        floatingLabelText={t('generic.modules')}
                        hintText={t('generic.modules')}
                        fullWidth={true}
                    >
                        {modules}
                    </Field>
                    <Field
                        id="default_language"
                        name="default_language"
                        component={SelectField}
                        multiple={false}
                        floatingLabelText={t('page_app.default_lang')}
                        hintText={t('page_app.default_lang')}
                        fullWidth={true}
                    >
                        {languages}
                    </Field>
                    <Field
                        id="center_latitude"
                        name="center_latitude"
                        component={renderTextField}
                        label={t('page_app.center_latitude')}
                        fullWidth={true}
                        type={'number'}
                        normalize={(value) => {
                            return parseFloat(value) || 0;
                        }}
                    />
                    <Field
                        id="center_longitude"
                        name="center_longitude"
                        component={renderTextField}
                        label={t('page_app.center_longitude')}
                        fullWidth={true}
                        type={'number'}
                        normalize={(value) => {
                            return parseFloat(value) || 0;
                        }}
                    />
                    <Field
                        id="android_package_name"
                        name="android_package_name"
                        component={renderTextField}
                        label={t('page_app.android_package_name')}
                        fullWidth={true}
                    />

                    <Field
                        id="ios_bundle_id"
                        name="ios_bundle_id"
                        component={renderTextField}
                        label={t('page_app.ios_bundle_id')}
                        fullWidth={true}
                    />

                    <Field
                        id="ios_app_store_id"
                        name="ios_app_store_id"
                        component={renderTextField}
                        label={t('page_app.ios_app_store_id')}
                        fullWidth={true}
                    />
                </div>
                <div style={styles.formButtons}>
                    <RaisedButton
                        style={styles.formButton}
                        label={t('generic.save')}
                        disabled={pristine || submitting}
                        type="submit"
                        primary={true}
                    />

                    <RaisedButton
                        style={styles.formButton}
                        label={t('generic.reset')}
                        type="button"
                        disabled={pristine || submitting}
                        onClick={() => {
                            reset();
                            if (this.props.onFormReset) {
                                this.props.onFormReset();
                            }
                        }}
                    />
                </div>
            </form>
        );
    }
};

export default reduxForm({
    form: 'editApplication',
    validate,
})(withTranslation(ApplicationForm));



// WEBPACK FOOTER //
// src/components/application/ApplicationForm/ApplicationForm.js